/**
 * Connected layer: wires the i18n-free components to @platform/i18n, the user preferences and
 * the instance branding / language packs from the platform API. Every app renders
 * `<PlatformProvider>` once at its root, nothing else is needed.
 */
import type { PublicBranding } from '@platform/branding';
import { I18nProvider, useI18n, type FlatMessages, type LanguageInfo, type MessageKey } from '@platform/i18n';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Toaster } from '../components/sonner';
import { TooltipProvider } from '../components/tooltip';
import { PreferencesProvider } from '../hooks/use-preferences';
import { cacheBranding, readCachedBranding } from '../lib/brand';
import { UiStringsProvider, uiStringsFor } from '../strings';
import { BrandingContext, BrandingEffects, type BrandingContextValue, type PlatformRuntime } from './branding';

function UiStringsBridge({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const strings = useMemo(() => uiStringsFor(t), [t]);
  return <UiStringsProvider strings={strings}>{children}</UiStringsProvider>;
}

function AppTitle({ titleKey }: { titleKey: MessageKey }) {
  const { t } = useI18n();
  return <BrandingEffects appName={t(titleKey)} />;
}

interface Languages {
  defaultLocale: string;
  languages: readonly (LanguageInfo & { hash: string })[];
}

export interface PlatformProviderProps {
  children: ReactNode;
  /** Pack key of the app name for the document title, e.g. `admin.appName`. */
  appNameKey: MessageKey;
  /** Public API of the platform; omitted = built-in branding and languages only (mock/preview). */
  runtime?: PlatformRuntime;
}

export function PlatformProvider({ children, appNameKey, runtime }: PlatformProviderProps) {
  const [branding, setBrandingState] = useState<PublicBranding | null>(() => (runtime ? readCachedBranding() : null));
  const [languages, setLanguages] = useState<Languages>();
  const [languagesVersion, setLanguagesVersion] = useState(0);
  const [locale, setLocale] = useState<string>();
  const [messages, setMessages] = useState<Readonly<Record<string, { hash: string; messages: FlatMessages }>>>({});

  const setBranding = useCallback((next: PublicBranding) => {
    setBrandingState(next);
    cacheBranding(next);
  }, []);

  useEffect(() => {
    let active = true;
    runtime?.branding().then(
      (next) => {
        if (active) setBranding(next);
      },
      // Unreachable API: keep the cached/built-in look; the login reports the error.
      () => undefined,
    );
    return () => {
      active = false;
    };
  }, [runtime, setBranding]);

  useEffect(() => {
    let active = true;
    runtime?.languages().then(
      (next) => {
        if (active) setLanguages(next);
      },
      () => undefined,
    );
    return () => {
      active = false;
    };
  }, [runtime, languagesVersion]);

  // Runtime messages of the current language and of English (English overrides fill gaps everywhere).
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (!runtime || !languages || !locale) return;
    for (const code of new Set([locale, 'en'])) {
      const entry = languages.languages.find((language) => language.locale === code);
      const key = `${code}@${entry?.hash ?? ''}`;
      if (!entry || requested.current.has(key)) continue;
      requested.current.add(key);
      runtime.messages(code).then(
        (result) => setMessages((current) => ({ ...current, [code]: { hash: result.hash, messages: result.messages } })),
        () => requested.current.delete(key),
      );
    }
  }, [runtime, languages, locale]);

  const catalogues = useMemo(() => Object.fromEntries(Object.entries(messages).map(([code, entry]) => [code, entry.messages])), [messages]);

  const context = useMemo<BrandingContextValue>(
    () => ({
      branding,
      setBranding,
      reloadLanguages: () => setLanguagesVersion((version) => version + 1),
      assetUrl: (path) => (runtime ? runtime.url(path) : path),
    }),
    [branding, setBranding, runtime],
  );

  const defaults = useMemo(
    () => (branding ? { theme: branding.settings.defaultTheme, density: branding.settings.defaultDensity } : undefined),
    [branding],
  );

  return (
    <BrandingContext.Provider value={context}>
      <I18nProvider languages={languages?.languages} defaultLocale={languages?.defaultLocale} runtime={catalogues} onLocaleChange={setLocale}>
        <PreferencesProvider defaults={defaults}>
          <AppTitle titleKey={appNameKey} />
          <UiStringsBridge>
            <TooltipProvider delayDuration={300}>
              {children}
              <Toaster />
            </TooltipProvider>
          </UiStringsBridge>
        </PreferencesProvider>
      </I18nProvider>
    </BrandingContext.Provider>
  );
}
